/* eslint-disable sonarjs/cognitive-complexity */
import { useAuth } from '@ahm/react-auth';
import { FloatingLabelInput } from '@ahm/react-ui';
import { Button, Card, Group, Skeleton, Stack, Text, rem } from '@mantine/core';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import PathInspectionPolicy from './path-inspection-policy';
import { apiRoutes } from '@/api/api-routes';
import { type RecentContact, useGetRecentContact } from '@/api/order';
import { ROUTES } from '@/config/router';
import { ActionIndexPath } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type RecipientSenderDetailBlockProps = {
  pathIndex: number;
};

export default function PathRecipientInfo({
  pathIndex,
}: RecipientSenderDetailBlockProps) {
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
    watch,
  } = useFormContext();
  const tBooking = useTranslations('booking');
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const { selectedService } = useBookingStore((state) => state);
  const { session } = useAuth();
  const user = session?.user;

  const { data: contactSuggestions, isLoading: isLoadingContacts } =
    useGetRecentContact({
      enabled: !!user,
      queryKey: [apiRoutes.order.recentContact, user?._id],
    });

  const isPartnerMode = useCallback(() => {
    return (selectedService?.partner ?? user?.partner) ? true : false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, user]);

  const trackingNumberFieldVisible = () => {
    return pathIndex !== 0 && isPartnerMode();
  };

  const handleFillMyProfile = async (contact?: RecentContact) => {
    if (contact) {
      setValue('name', contact.name);
      setValue('mobile', contact.mobile?.replace(/^84/, '0'));
      await trigger(['name', 'mobile']);
    } else if (user) {
      setValue('name', user.name);
      setValue('mobile', user._id?.replace(/^84/, '0'));
      await trigger(['name', 'mobile']);
    }
  };

  const canFillMyProfile = useMemo(() => {
    if (!user) return false;

    const formName = watch('name');
    const formMobile = watch('mobile');
    return !formName || !formMobile || formName !== user.name;
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [watch('name'), watch('mobile'), user]);

  const isNationwide = useMemo(() => {
    return pathname === ROUTES.nationwide;
  }, [pathname]);

  const isPickup = useMemo(() => {
    return pathIndex === ActionIndexPath.Pickup;
  }, [pathIndex]);

  const filteredContacts = useMemo(() => {
    if (!Array.isArray(contactSuggestions)) return [];

    const contactType = isPickup ? 'sender' : 'recipient';
    return contactSuggestions.filter((contact) => contact.type === contactType);
  }, [contactSuggestions, isPickup]);

  return (
    <Card radius={0} px="md">
      <Card.Section inheritPadding py="sm">
        <Group wrap="nowrap" justify="space-between">
          <Text fz={16} fw={700} lts={0.5} c="neutral.7" flex={1}>
            {isPickup
              ? tBooking('location.sender_details')
              : tBooking('location.recipient_details')}
          </Text>
          {canFillMyProfile ? (
            <Button
              size="compact-sm"
              variant="subtle"
              color="secondary.5"
              fw={700}
              mr={-8}
              flex="none"
              onClick={() => handleFillMyProfile()}
            >
              {isPickup
                ? tBooking('location.i_am_the_sender')
                : tBooking('location.i_am_the_recipient')}
            </Button>
          ) : null}
        </Group>
      </Card.Section>

      <Stack gap={isMobile ? rem(16) : rem(8)} pt={0}>
        <Controller
          name="name"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FloatingLabelInput
              label={
                pathIndex === ActionIndexPath.Pickup
                  ? tBooking('location.sender_name')
                  : tBooking('location.recipient_name')
              }
              h="auto"
              withAsterisk={!isPickup}
              value={value}
              onChange={(e) => onChange(e)}
              error={errors.name?.message as string}
            />
          )}
        />
        <Controller
          name="mobile"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FloatingLabelInput
              label={tBooking('location.phone_number')}
              h={'auto'}
              withAsterisk={pathIndex !== ActionIndexPath.Pickup}
              value={value}
              onChange={(e) => onChange(e)}
              error={errors.mobile?.message as string}
            />
          )}
        />

        {isLoadingContacts ? (
          <Group gap={8} wrap="nowrap">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton
                key={index}
                height={rem(44)}
                width={rem(140)}
                radius="md"
              />
            ))}
          </Group>
        ) : filteredContacts.length > 0 ? (
          <Stack gap={8}>
            <Text size={isMobile ? 'sm' : 'md'} c="neutral.6" fw={600}>
              {isPickup
                ? tBooking('location.sender_recent_contacts')
                : tBooking('location.recipient_recent_contacts')}
            </Text>

            <div className="scrollbar-hover min-h-[60px] overflow-x-auto overflow-y-hidden">
              <Group
                gap={8}
                wrap="nowrap"
                className="!inline-flex w-max !flex-nowrap pr-2"
              >
                {filteredContacts.map((contact) => (
                  <Button
                    key={contact.mobile}
                    variant="subtle"
                    bg="neutral.0"
                    h={rem(44)}
                    styles={{
                      root: {
                        padding: `${rem(4)} ${rem(8)}`,
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleFillMyProfile(contact);
                    }}
                  >
                    <Stack gap={0} align="flex-start">
                      <Text
                        size={isMobile ? 'xs' : 'sm'}
                        fw={600}
                        c="neutral.7"
                        truncate
                        maw={rem(140)}
                      >
                        {contact.name}
                      </Text>
                      <Text
                        size={isMobile ? 'xs' : 'sm'}
                        fw={500}
                        c="neutral.6"
                        truncate
                        maw={rem(140)}
                      >
                        {contact.mobile}
                      </Text>
                    </Stack>
                  </Button>
                ))}
              </Group>
            </div>
          </Stack>
        ) : null}

        {!isPickup ? (
          <>
            {trackingNumberFieldVisible() ? (
              <Controller
                name="tracking_number"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FloatingLabelInput
                    h={'auto'}
                    label={tBooking('location.tracking_number')}
                    value={value}
                    onChange={(e) => onChange(e)}
                  />
                )}
              />
            ) : null}

            {isNationwide ? <PathInspectionPolicy /> : null}
          </>
        ) : null}
      </Stack>
    </Card>
  );
}
